import { useCallback, useMemo, useState } from 'react'

import { toFilterOptionIds } from './filterOptionIds'
import {
  IFacet,
  IFacetOption,
  IUseFiltersOptions,
  IUseFiltersResult,
} from './types'

const defaultIdAccessor = <T extends { id: string }>(item: T) => item.id

export function useFilters<T extends { id: string }>({
  items,
  fields,
  idAccessor = defaultIdAccessor,
}: IUseFiltersOptions<T>): IUseFiltersResult<T> {
  const [appliedFilters, setAppliedFilters] = useState<
    Record<string, Set<string>>
  >({})
  const [searchKey, setSearchKey] = useState('')

  const facetFields = useMemo(
    () => fields.filter(field => field.isFacetable),
    [fields],
  )

  const searchableFields = useMemo(
    () => fields.filter(field => field.isSearchable),
    [fields],
  )

  const facets = useMemo(
    (): IFacet[] =>
      facetFields.map(field => ({
        key: field.key,
        label: field.label,
        isHiddenInFilterUI: field.isHiddenInFilterUI,
      })),
    [facetFields],
  )

  const sourceMapByFacet = useMemo(() => {
    const result: Record<string, Record<string, string[]>> = {}

    facetFields.forEach(field => {
      const optionToIds: Record<string, string[]> = {}

      items.forEach(item => {
        const raw = field.accessor(item)
        const itemId = idAccessor(item)
        const optionIds = toFilterOptionIds(raw)

        optionIds.forEach(optionId => {
          if (!optionToIds[optionId]) {
            optionToIds[optionId] = []
          }
          optionToIds[optionId].push(itemId)
        })
      })

      result[field.key] = optionToIds
    })

    return result
  }, [items, facetFields, idAccessor])

  const getFacetOptions = useCallback(
    (fieldKey: string): IFacetOption[] => {
      const field = facetFields.find(f => f.key === fieldKey)
      const sourceMap = sourceMapByFacet[fieldKey]

      if (!field || !sourceMap) return []

      let allowedIds = new Set(items.map(item => idAccessor(item)))

      Object.entries(appliedFilters).forEach(
        ([appliedFieldKey, selectedIds]) => {
          if (appliedFieldKey === fieldKey || selectedIds.size === 0) {
            return
          }

          const appliedSourceMap = sourceMapByFacet[appliedFieldKey]
          if (!appliedSourceMap) {
            return
          }

          const nextAllowedIds = new Set<string>()

          selectedIds.forEach(optionId => {
            const itemIds = appliedSourceMap[optionId]
            if (!itemIds) {
              return
            }

            itemIds.forEach(itemId => {
              if (allowedIds.has(itemId)) {
                nextAllowedIds.add(itemId)
              }
            })
          })

          allowedIds = nextAllowedIds
        },
      )

      return Object.entries(sourceMap).map(([optionId, itemIds]) => ({
        id: optionId,
        label: field.displayFormatter
          ? field.displayFormatter(optionId)
          : optionId,
        count: itemIds.filter(itemId => allowedIds.has(itemId)).length,
        parentValue: field.getOptionParentValue
          ? (field.getOptionParentValue(optionId) ?? undefined)
          : undefined,
        ownLabel: field.getOptionLabel
          ? (field.getOptionLabel(optionId) ?? undefined)
          : undefined,
      }))
    },
    [items, idAccessor, facetFields, sourceMapByFacet, appliedFilters],
  )

  const setFilter = useCallback((fieldKey: string, selectedIds: string[]) => {
    setAppliedFilters(prev => {
      const next = { ...prev }
      if (selectedIds.length === 0) {
        delete next[fieldKey]
      } else {
        next[fieldKey] = new Set(selectedIds)
      }
      return next
    })
  }, [])

  const resetAll = useCallback(() => {
    setAppliedFilters({})
    setSearchKey('')
  }, [])

  const filteredItems = useMemo(() => {
    let result = items

    const activeFilterKeys = Object.keys(appliedFilters).filter(
      key => appliedFilters[key].size > 0,
    )

    if (activeFilterKeys.length > 0) {
      result = result.filter(item => {
        const itemId = idAccessor(item)

        return activeFilterKeys.every(fieldKey => {
          const allowedIds = appliedFilters[fieldKey]
          const sourceMap = sourceMapByFacet[fieldKey]

          if (!sourceMap || !allowedIds) return true

          for (const optionId of allowedIds) {
            const itemIds = sourceMap[optionId]
            if (itemIds && itemIds.includes(itemId)) {
              return true
            }
          }
          return false
        })
      })
    }

    if (searchKey) {
      const lowerSearch = searchKey.toLowerCase()
      result = result.filter(item => {
        return searchableFields.some(field => {
          const rawValue = (field.searchAccessor || field.accessor)(item)
          if (rawValue == null) return false

          const searchValues = Array.isArray(rawValue)
            ? rawValue.map(value => String(value))
            : [String(rawValue)]

          return searchValues.some(value =>
            value.toLowerCase().includes(lowerSearch),
          )
        })
      })
    }

    return result
  }, [
    items,
    appliedFilters,
    sourceMapByFacet,
    idAccessor,
    searchKey,
    searchableFields,
  ])

  const hasActiveFilters = useMemo(
    () =>
      Object.keys(appliedFilters).some(key => appliedFilters[key].size > 0) ||
      searchKey.length > 0,
    [appliedFilters, searchKey],
  )

  return {
    appliedFilters,
    searchKey,
    setFilter,
    setSearchKey,
    resetAll,
    filteredItems,
    hasActiveFilters,
    getFacetOptions,
    facets,
  }
}
